import type { GitpodAPI } from "@/api";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { defaultRefetchLists, defaultRetry, defaultThrowOnError } from "@/queries/errors";
import { autoPaginate, DEFAULT_PAGE_SIZE } from "@/queries/pagination";
import { keyWithPrincipal } from "@/queries/principal-key";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { DEFAULT_WORKFLOW_LIMITS } from "@/utils/automations";
import { toPlainMessage, type PlainMessage } from "@bufbuild/protobuf";
import { Code, ConnectError } from "@connectrpc/connect";
import {
    keepPreviousData,
    queryOptions,
    useMutation,
    useQuery,
    useQueryClient,
    type QueryClient,
} from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import { useCursorPagination } from "@/hooks/use-cursor-pagination";
import { PaginationRequest, Sort, SortOrder } from "gitpod-next-api/gitpod/v1/pagination_pb";
import { ResourceOperation, ResourceType, type WatchEventsResponse } from "gitpod-next-api/gitpod/v1/event_pb";
import {
    CancelWorkflowExecutionRequest,
    CancelWorkflowExecutionActionRequest,
    CreateWorkflowRequest,
    DeleteWorkflowRequest,
    UpdateWorkflowRequest,
    ListWorkflowExecutionActionsRequest,
    ListWorkflowExecutionOutputsRequest,
    type ListWorkflowExecutionOutputsResponse_ActionOutput,
    StartWorkflowRequest,
    WorkflowAction,
    WorkflowAction_Limits,
    WorkflowExecution,
    WorkflowExecutionAction,
    WorkflowExecutionActionPhase,
    WorkflowStep,
    WorkflowTrigger,
    WorkflowTrigger_Manual,
    WorkflowTriggerContext,
    WorkflowTriggerContext_Projects,
    type Workflow,
    WorkflowExecutionPhase,
} from "gitpod-next-api/gitpod/v1/workflow_pb";
import { type Subject } from "gitpod-next-api/gitpod/v1/identity_pb";
import { useOrganization } from "@/queries/organization-queries";
import { useResourcePermission } from "@/hooks/use-resource-permission";

export type PlainWorkflow = PlainMessage<Workflow>;
export type PlainWorkflowTrigger = PlainMessage<WorkflowTrigger>;
export type PlainWorkflowAction = PlainMessage<WorkflowAction>;
export type PlainWorkflowStep = PlainMessage<WorkflowStep>;
export type PlainWorkflowExecution = PlainMessage<WorkflowExecution>;
export type PlainWorkflowExecutionAction = PlainMessage<WorkflowExecutionAction>;

export function toPlainWorkflow(workflow: Workflow): PlainWorkflow {
    return toPlainMessage(workflow);
}

export const workflowQueryKey = {
    // Automations
    automations: {
        get: (workflowId?: string) => keyWithPrincipal(["automations", { workflowId }]),
        list: (filter?: WorkflowsFilter, page?: number) =>
            keyWithPrincipal(["automations", "list", { filter: filter ?? {}, page }]),
        cursorPage: (filter?: WorkflowsFilter, token?: string) =>
            keyWithPrincipal(["automations", "cursor", { filter: filter ?? {}, token }]),
    },
    webhookSecret: (workflowId?: string) => keyWithPrincipal(["automations", "webhook-secret", { workflowId }]),
    // Workflow Executions
    executions: {
        get: (workflowExecutionId?: string) => keyWithPrincipal(["workflow-executions", { workflowExecutionId }]),
        list: (workflowId: string, filter?: WorkflowExecutionsFilter, page?: number) =>
            keyWithPrincipal(["workflow-executions", "list", workflowId, { filter, page }]),
        cursorPage: (workflowId: string, filter?: WorkflowExecutionsFilter, token?: string) =>
            keyWithPrincipal(["workflow-executions", "cursor", workflowId, { filter, token }]),
        mostRecent: (workflowId: string) => keyWithPrincipal(["workflow-executions", "most-recent", workflowId]),
    },
    // Workflow Execution Actions
    executionActions: (workflowExecutionId: string) =>
        keyWithPrincipal(["workflow-execution-actions", "list", { workflowExecutionId }]),
    executionAction: (workflowExecutionActionId?: string) =>
        keyWithPrincipal(["workflow-execution-actions", { workflowExecutionActionId }]),
    executionOutputs: (workflowExecutionId: string) =>
        keyWithPrincipal(["workflow-execution-outputs", { workflowExecutionId }]),
};

// Helper to get the list/cursor key prefixes for invalidation (slice off the filter/page/token params)
const automationsListKeyPrefix = () => workflowQueryKey.automations.list().slice(0, -1);
const automationsCursorKeyPrefix = () => workflowQueryKey.automations.cursorPage().slice(0, -1);
const executionsListKeyPrefix = (workflowId: string) => workflowQueryKey.executions.list(workflowId).slice(0, -1);
const executionsCursorKeyPrefix = (workflowId: string) =>
    workflowQueryKey.executions.cursorPage(workflowId).slice(0, -1);

export const handleWorkflowEvents = async (api: GitpodAPI, client: QueryClient, evt: WatchEventsResponse) => {
    switch (evt.resourceType) {
        case ResourceType.WORKFLOW: {
            if (evt.operation === ResourceOperation.UPDATE || evt.operation === ResourceOperation.UPDATE_STATUS) {
                const workflow = await refetchWorkflow(api, evt.resourceId);
                setWorkflowInCache(client, evt.resourceId, workflow ? toPlainWorkflow(workflow) : undefined);
                await client.invalidateQueries({ queryKey: automationsListKeyPrefix() });
                await client.invalidateQueries({ queryKey: automationsCursorKeyPrefix() });
            }
            if (evt.operation === ResourceOperation.CREATE) {
                await client.invalidateQueries({ queryKey: automationsListKeyPrefix() });
                await client.invalidateQueries({ queryKey: automationsCursorKeyPrefix() });
            }
            if (evt.operation === ResourceOperation.DELETE) {
                setWorkflowInCache(client, evt.resourceId, undefined);
                await client.invalidateQueries({ queryKey: automationsListKeyPrefix() });
                await client.invalidateQueries({ queryKey: automationsCursorKeyPrefix() });
            }
            break;
        }
        case ResourceType.WORKFLOW_EXECUTION: {
            if (evt.operation === ResourceOperation.UPDATE || evt.operation === ResourceOperation.UPDATE_STATUS) {
                // Get workflowId from cached execution before invalidating
                const cachedExecution = client.getQueryData<PlainWorkflowExecution>(
                    workflowQueryKey.executions.get(evt.resourceId),
                );
                const workflowId = cachedExecution?.metadata?.workflowId;

                // Invalidate individual execution cache
                await client.invalidateQueries({
                    queryKey: workflowQueryKey.executions.get(evt.resourceId),
                });

                // Invalidate list and cursor queries (status changes may affect filtered views)
                if (workflowId) {
                    await client.invalidateQueries({
                        queryKey: executionsListKeyPrefix(workflowId),
                    });
                    await client.invalidateQueries({
                        queryKey: executionsCursorKeyPrefix(workflowId),
                    });
                    await client.invalidateQueries({
                        queryKey: workflowQueryKey.executions.mostRecent(workflowId),
                    });
                }
            }
            if (evt.operation === ResourceOperation.CREATE) {
                // We need to fetch the execution so we can get the workflow ID
                const execution = await refetchWorkflowExecution(api, evt.resourceId);
                if (!execution) {
                    return;
                }

                // Add it to the cache
                const plainExecution = toPlainMessage(execution);
                setWorkflowExecutionInCache(client, {
                    workflowExecutionId: plainExecution.id,
                    workflowExecution: plainExecution,
                });

                // Invalidate the list and cursor queries for the workflow
                const workflowId = execution?.metadata?.workflowId;
                if (!execution || !workflowId) {
                    return;
                }
                await client.invalidateQueries({
                    queryKey: executionsListKeyPrefix(workflowId),
                });
                await client.invalidateQueries({
                    queryKey: executionsCursorKeyPrefix(workflowId),
                });
                await client.invalidateQueries({
                    queryKey: workflowQueryKey.executions.mostRecent(workflowId),
                });
            }
            if (evt.operation === ResourceOperation.DELETE) {
                const cachedExecution = client.getQueryData<PlainWorkflowExecution>(
                    workflowQueryKey.executions.get(evt.resourceId),
                );
                const workflowId = cachedExecution?.metadata?.workflowId;
                if (!cachedExecution || !workflowId) {
                    return;
                }

                // Remove it from the cache
                setWorkflowExecutionInCache(client, {
                    workflowExecutionId: cachedExecution.id,
                    workflowExecution: undefined,
                });

                // Invalidate the lists and cursor queries
                if (workflowId) {
                    await client.invalidateQueries({
                        queryKey: executionsListKeyPrefix(workflowId),
                    });
                    await client.invalidateQueries({
                        queryKey: executionsCursorKeyPrefix(workflowId),
                    });
                    await client.invalidateQueries({
                        queryKey: workflowQueryKey.executions.mostRecent(workflowId),
                    });
                }
            }
            break;
        }
        case ResourceType.WORKFLOW_EXECUTION_ACTION: {
            if (evt.operation === ResourceOperation.UPDATE || evt.operation === ResourceOperation.UPDATE_STATUS) {
                const action = await refetchWorkflowExecutionAction(api, evt.resourceId);
                if (action?.metadata?.workflowExecutionId) {
                    setWorkflowExecutionActionInCache(client, {
                        workflowExecutionId: action.metadata.workflowExecutionId,
                        workflowExecutionActionId: evt.resourceId,
                        action: action ? toPlainMessage(action) : undefined,
                    });
                }
            }
            if (evt.operation === ResourceOperation.CREATE) {
                const action = await refetchWorkflowExecutionAction(api, evt.resourceId);
                if (action?.metadata?.workflowExecutionId) {
                    await client.invalidateQueries({
                        queryKey: workflowQueryKey.executionActions(action?.metadata?.workflowExecutionId),
                    });
                }
            }
            if (evt.operation === ResourceOperation.DELETE) {
                const cachedAction = client.getQueryData<PlainWorkflowExecutionAction>(
                    workflowQueryKey.executionAction(evt.resourceId),
                );
                if (cachedAction?.metadata?.workflowExecutionId) {
                    setWorkflowExecutionActionInCache(client, {
                        workflowExecutionId: cachedAction?.metadata?.workflowExecutionId,
                        workflowExecutionActionId: evt.resourceId,
                        action: undefined,
                    });
                    await client.invalidateQueries({
                        queryKey: workflowQueryKey.executionActions(cachedAction?.metadata?.workflowExecutionId),
                    });
                }
            }
            break;
        }
    }
};

const refetchWorkflow = async (api: GitpodAPI, workflowId: string) => {
    try {
        const response = await api.workflowService.getWorkflow({ workflowId });
        return response.workflow;
    } catch (error) {
        const isNotFound = error instanceof ConnectError && error.code === Code.NotFound;
        if (!isNotFound) {
            console.error("Failed to refetch workflow", workflowId);
        }
    }
};

const refetchWorkflowExecution = async (
    api: GitpodAPI,
    workflowExecutionId: string,
): Promise<WorkflowExecution | undefined> => {
    try {
        const response = await api.workflowService.getWorkflowExecution({ workflowExecutionId });
        return response.workflowExecution;
    } catch (error) {
        const isNotFound = error instanceof ConnectError && error.code === Code.NotFound;
        if (!isNotFound) {
            console.error("Failed to refetch workflow execution", workflowExecutionId);
        }
    }
};

const refetchWorkflowExecutionAction = async (api: GitpodAPI, workflowExecutionActionId: string) => {
    try {
        const response = await api.workflowService.getWorkflowExecutionAction({ workflowExecutionActionId });
        return response.workflowExecutionAction;
    } catch (error) {
        const isNotFound = error instanceof ConnectError && error.code === Code.NotFound;
        if (!isNotFound) {
            console.error("Failed to refetch workflow execution action", workflowExecutionActionId);
        }
    }
};

export const useGetWorkflow = (workflowId?: string) => {
    const api = useGitpodAPI();

    return useQuery({
        queryKey: workflowQueryKey.automations.get(workflowId),
        queryFn: async () => {
            if (!workflowId) {
                throw new Error("No workflowId provided");
            }

            const { workflow } = await api.workflowService.getWorkflow({
                workflowId,
            });

            if (!workflow) {
                throw new Error("Error fetching workflow");
            }

            return toPlainWorkflow(workflow);
        },
        enabled: !!workflowId,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export type WorkflowsFilter = {
    search?: string;
    creatorIds?: string[];
    statusPhases?: WorkflowExecutionPhase[];
};

export type UsePaginatedWorkflowsParams = {
    filter?: WorkflowsFilter;
    page?: number;
    enabled?: boolean;
};

type WorkflowListPageData = {
    workflowIds: string[];
    totalCount?: number;
    totalPages?: number;
    pageSize: number;
    nextToken?: string;
};

type WorkflowListPageOptionsParams = UsePaginatedWorkflowsParams & {
    queryClient: QueryClient;
};

const useGetWorkflowListPageOptions = ({
    filter,
    page = 0,
    enabled = true,
    queryClient,
}: WorkflowListPageOptionsParams) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    const queryKey = workflowQueryKey.automations.list(filter, page);
    return queryOptions({
        queryKey,
        queryFn: async (): Promise<WorkflowListPageData> => {
            const token = page === 0 ? "" : String(page);
            const response = await api.workflowService.listWorkflows({
                filter: {
                    search: filter?.search,
                    creatorIds: filter?.creatorIds,
                    statusPhases: filter?.statusPhases,
                },
                pagination: new PaginationRequest({ pageSize: DEFAULT_PAGE_SIZE, token }),
            });

            const workflows = response.workflows.map(toPlainWorkflow);
            const pageSize = DEFAULT_PAGE_SIZE;

            // Seed individual workflow caches with the full objects
            for (const workflow of workflows) {
                setWorkflowInCache(queryClient, workflow.id, workflow);
            }

            // Extract metadata from pagination token (format: "pageNumber/totalPages/totalCount")
            let totalPages: number | undefined;
            let totalCount: number | undefined;

            if (response.pagination?.nextToken) {
                const parts = response.pagination.nextToken.split("/");
                if (parts.length === 3) {
                    totalPages = parseInt(parts[1], 10);
                    totalCount = parseInt(parts[2], 10);
                }
            } else if (page === 0) {
                // No nextToken on page 0 means all data fits on one page
                totalPages = 1;
                totalCount = workflows.length;
            }

            // Return only IDs - full objects are in individual caches
            return {
                workflowIds: workflows.map((w) => w.id),
                totalCount,
                totalPages,
                pageSize,
                nextToken: response.pagination?.nextToken,
            };
        },
        enabled: enabled && !!user,
        staleTime: 5 * 60 * 1000,
        throwOnError: false,
        retry: defaultRetry,
        placeholderData: keepPreviousData,
    });
};

const useWorkflowListPage = ({ filter, page = 0, enabled = true }: UsePaginatedWorkflowsParams) => {
    const queryClient = useQueryClient();
    const options = useGetWorkflowListPageOptions({ filter, page, enabled, queryClient });
    return useQuery(options);
};

/**
 * Hook for page-based pagination of workflows.
 * Returns workflowIds, totalCount, totalPages, and pageSize for use with AdaptivePagination.
 */
export const usePaginatedWorkflows = ({ filter, page = 0, enabled = true }: UsePaginatedWorkflowsParams) => {
    const queryClient = useQueryClient();

    // Fetch the requested page
    const pageQuery = useWorkflowListPage({ filter, page, enabled });

    // Fetch page 0 for metadata (React Query deduplicates if page === 0)
    const page0Query = useWorkflowListPage({ filter, page: 0, enabled });

    // Use metadata from page 0, fall back to current page
    const metadata = {
        totalCount: page0Query.data?.totalCount ?? pageQuery.data?.totalCount,
        totalPages: page0Query.data?.totalPages ?? pageQuery.data?.totalPages ?? 0,
        pageSize: page0Query.data?.pageSize ?? DEFAULT_PAGE_SIZE,
        nextToken: pageQuery.data?.nextToken,
    };

    const nextPageOptions = useGetWorkflowListPageOptions({ filter, page: page + 1, enabled, queryClient });

    // Prefetch the next page
    useEffect(() => {
        void queryClient.prefetchQuery(nextPageOptions).catch(console.error);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, page, filter, queryClient]);

    const workflowIds = useMemo(() => {
        return pageQuery.data?.workflowIds ?? [];
    }, [pageQuery.data]);

    return {
        ...pageQuery,
        workflowIds,
        ...metadata,
    };
};

type UseWorkflowsCursorPageParams = {
    filter?: WorkflowsFilter;
    enabled?: boolean;
};

type WorkflowsCursorPageData = {
    workflowIds: string[];
    nextToken: string;
};

/**
 * Fetches a single page of workflows using an opaque cursor token.
 * Manages cursor state internally and resets when filters change.
 */
export const useWorkflowsCursorPage = ({ filter, enabled = true }: UseWorkflowsCursorPageParams) => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();
    const { data: user } = useAuthenticatedUser();
    const cursor = useCursorPagination();

    // Reset cursor when filters change
    const filterKey = JSON.stringify(filter);
    const prevFilterKeyRef = useRef(filterKey);
    useEffect(() => {
        if (prevFilterKeyRef.current !== filterKey) {
            prevFilterKeyRef.current = filterKey;
            cursor.reset();
        }
    }, [filterKey, cursor]);

    const query = useQuery({
        queryKey: workflowQueryKey.automations.cursorPage(filter, cursor.currentToken),
        queryFn: async (): Promise<WorkflowsCursorPageData> => {
            const response = await api.workflowService.listWorkflows({
                filter: {
                    search: filter?.search,
                    creatorIds: filter?.creatorIds,
                    statusPhases: filter?.statusPhases,
                },
                pagination: new PaginationRequest({ pageSize: DEFAULT_PAGE_SIZE, token: cursor.currentToken }),
            });

            const workflows = response.workflows.map(toPlainWorkflow);

            // Seed individual workflow caches
            for (const workflow of workflows) {
                setWorkflowInCache(queryClient, workflow.id, workflow);
            }

            return {
                workflowIds: workflows.map((w) => w.id),
                nextToken: response.pagination?.nextToken ?? "",
            };
        },
        enabled: enabled && !!user,
        staleTime: 5 * 60 * 1000,
        throwOnError: false,
        retry: defaultRetry,
        placeholderData: keepPreviousData,
    });

    return { ...query, cursor };
};

export type WorkflowExecutionsFilter = {
    statusPhases?: WorkflowExecutionPhase[];
    hasFailedActions?: boolean;
    search?: string;
};

export type UseGetMostRecentExecutionParams = {
    enabled: boolean;
    workflowId: string;
};

export const useGetMostRecentExecutionId = ({ enabled, workflowId }: UseGetMostRecentExecutionParams) => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: workflowQueryKey.executions.mostRecent(workflowId),
        queryFn: async (): Promise<string | null> => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            const response = await api.workflowService.listWorkflowExecutions({
                filter: {
                    workflowIds: [workflowId],
                    statusPhases: [
                        WorkflowExecutionPhase.PENDING,
                        WorkflowExecutionPhase.RUNNING,
                        WorkflowExecutionPhase.STOPPING,
                        WorkflowExecutionPhase.STOPPED,
                        WorkflowExecutionPhase.COMPLETED,
                    ],
                },
                sort: new Sort({
                    field: "startedAt",
                    order: SortOrder.DESC,
                }),
                pagination: new PaginationRequest({ pageSize: 1 }),
            });

            if (response.workflowExecutions.length === 0) {
                return null;
            }

            const execution = toPlainMessage(response.workflowExecutions[0]);

            // Seed individual execution cache
            setWorkflowExecutionInCache(queryClient, {
                workflowExecutionId: execution.id,
                workflowExecution: execution,
            });

            return execution.id;
        },
        enabled: enabled && !!user && !!workflowId,
        throwOnError: false,
        retry: defaultRetry,
        refetchOnReconnect: "always",
        refetchOnWindowFocus: "always",
        refetchIntervalInBackground: true,
        refetchInterval: defaultRefetchLists,
    });
};

export type WorkflowExecutionListPageData = {
    executionIds: string[];
    totalCount?: number;
    totalPages?: number;
    pageSize: number;
    nextToken?: string;
};

export type UsePaginatedWorkflowExecutionsParams = {
    enabled: boolean;
    workflowId: string;
    filter?: WorkflowExecutionsFilter;
    page?: number;
};

type WorkflowExecutionListPageOptionsParams = UsePaginatedWorkflowExecutionsParams & {
    queryClient: QueryClient;
};

const useGetWorkflowExecutionListPageOptions = ({
    workflowId,
    filter,
    page = 0,
    enabled = true,
    queryClient,
}: WorkflowExecutionListPageOptionsParams) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    const queryKey = workflowQueryKey.executions.list(workflowId, filter, page);
    return queryOptions({
        queryKey,
        queryFn: async (): Promise<WorkflowExecutionListPageData> => {
            const token = page === 0 ? "" : String(page);
            const response = await api.workflowService.listWorkflowExecutions({
                filter: {
                    workflowIds: [workflowId],
                    statusPhases: filter?.statusPhases,
                    hasFailedActions: filter?.hasFailedActions,
                    search: filter?.search,
                },
                pagination: new PaginationRequest({ pageSize: DEFAULT_PAGE_SIZE, token }),
            });

            const executions = response.workflowExecutions.map((execution) => toPlainMessage(execution));
            const pageSize = DEFAULT_PAGE_SIZE;

            // Seed individual execution caches with the full objects
            for (const execution of executions) {
                setWorkflowExecutionInCache(queryClient, {
                    workflowExecutionId: execution.id,
                    workflowExecution: execution,
                });
            }

            // Extract metadata from pagination token (format: "pageNumber/totalPages/totalCount")
            let totalPages: number | undefined;
            let totalCount: number | undefined;

            if (response.pagination?.nextToken) {
                const parts = response.pagination.nextToken.split("/");
                if (parts.length === 3) {
                    totalPages = parseInt(parts[1], 10);
                    totalCount = parseInt(parts[2], 10);
                }
            } else if (page === 0) {
                // No nextToken on page 0 means all data fits on one page
                totalPages = 1;
                totalCount = executions.length;
            }

            // Return only IDs - full objects are in individual caches
            return {
                executionIds: executions.map((e) => e.id),
                totalCount,
                totalPages,
                pageSize,
                nextToken: response.pagination?.nextToken,
            };
        },
        enabled: enabled && !!user && !!workflowId,
        staleTime: 5 * 60 * 1000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        placeholderData: keepPreviousData,
    });
};

/**
 * Simple hook that fetches a single page of workflow executions.
 */
const useWorkflowExecutionListPage = ({
    workflowId,
    filter,
    page = 0,
    enabled = true,
}: UsePaginatedWorkflowExecutionsParams) => {
    const queryClient = useQueryClient();
    const options = useGetWorkflowExecutionListPageOptions({ workflowId, filter, page, enabled, queryClient });
    return useQuery(options);
};

/**
 * Hook for page-based pagination of workflow executions.
 * Returns executionIds, totalCount, totalPages, and pageSize for use with AdaptivePagination.
 */
export const usePaginatedWorkflowExecutions = ({
    workflowId,
    filter,
    page = 0,
    enabled = true,
}: UsePaginatedWorkflowExecutionsParams) => {
    const queryClient = useQueryClient();

    // Fetch the requested page
    const pageQuery = useWorkflowExecutionListPage({ workflowId, filter, page, enabled });

    // Fetch page 0 for metadata (React Query deduplicates if page === 0)
    const page0Query = useWorkflowExecutionListPage({ workflowId, filter, page: 0, enabled });

    // Use metadata from page 0, fall back to current page
    const metadata = {
        totalCount: page0Query.data?.totalCount ?? pageQuery.data?.totalCount,
        totalPages: page0Query.data?.totalPages ?? pageQuery.data?.totalPages ?? 0,
        pageSize: page0Query.data?.pageSize ?? DEFAULT_PAGE_SIZE,
        nextToken: pageQuery.data?.nextToken,
    };

    const nextPageOptions = useGetWorkflowExecutionListPageOptions({
        workflowId,
        filter,
        page: page + 1,
        enabled,
        queryClient,
    });

    // Prefetch the next page
    useEffect(() => {
        void queryClient.prefetchQuery(nextPageOptions).catch(console.error);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, page, workflowId, filter, queryClient]);

    const executionIds = useMemo(() => {
        return pageQuery.data?.executionIds ?? [];
    }, [pageQuery.data]);

    return {
        ...pageQuery,
        executionIds,
        ...metadata,
    };
};

type UseWorkflowExecutionsCursorPageParams = {
    enabled: boolean;
    workflowId: string;
    filter?: WorkflowExecutionsFilter;
};

type WorkflowExecutionsCursorPageData = {
    executionIds: string[];
    nextToken: string;
};

/**
 * Fetches a single page of workflow executions using an opaque cursor token.
 * Manages cursor state internally and resets when filters change.
 */
export const useWorkflowExecutionsCursorPage = ({
    workflowId,
    filter,
    enabled = true,
}: UseWorkflowExecutionsCursorPageParams) => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();
    const { data: user } = useAuthenticatedUser();
    const cursor = useCursorPagination();

    // Reset cursor when filters change
    const filterKey = JSON.stringify(filter);
    const prevFilterKeyRef = useRef(filterKey);
    useEffect(() => {
        if (prevFilterKeyRef.current !== filterKey) {
            prevFilterKeyRef.current = filterKey;
            cursor.reset();
        }
    }, [filterKey, cursor]);

    const query = useQuery({
        queryKey: workflowQueryKey.executions.cursorPage(workflowId, filter, cursor.currentToken),
        queryFn: async (): Promise<WorkflowExecutionsCursorPageData> => {
            const response = await api.workflowService.listWorkflowExecutions({
                filter: {
                    workflowIds: [workflowId],
                    statusPhases: filter?.statusPhases,
                    hasFailedActions: filter?.hasFailedActions,
                    search: filter?.search,
                },
                pagination: new PaginationRequest({ pageSize: DEFAULT_PAGE_SIZE, token: cursor.currentToken }),
            });

            const executions = response.workflowExecutions.map((execution) => toPlainMessage(execution));

            // Seed individual execution caches
            for (const execution of executions) {
                setWorkflowExecutionInCache(queryClient, {
                    workflowExecutionId: execution.id,
                    workflowExecution: execution,
                });
            }

            return {
                executionIds: executions.map((e) => e.id),
                nextToken: response.pagination?.nextToken ?? "",
            };
        },
        enabled: enabled && !!user && !!workflowId,
        staleTime: 5 * 60 * 1000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        placeholderData: keepPreviousData,
    });

    return { ...query, cursor };
};

export type UseListWorkflowExecutionActionsParams = {
    enabled: boolean;
    workflowExecutionId: string;
};

export const useListWorkflowExecutionActions = ({
    enabled,
    workflowExecutionId,
}: UseListWorkflowExecutionActionsParams) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: workflowQueryKey.executionActions(workflowExecutionId),
        queryFn: async (): Promise<PlainWorkflowExecutionAction[]> => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            const request = new ListWorkflowExecutionActionsRequest({
                filter: { workflowExecutionIds: [workflowExecutionId] },
            });
            const workflowExecutionActions = await autoPaginate(
                (params) => api.workflowService.listWorkflowExecutionActions(params),
                request,
                (response) => response.workflowExecutionActions,
            );
            return workflowExecutionActions.map((action) => toPlainMessage(action));
        },
        enabled: enabled && !!user,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        refetchOnReconnect: "always",
        refetchOnWindowFocus: "always",
        refetchIntervalInBackground: true,
        refetchInterval: defaultRefetchLists,
    });
};

export const useGetWorkflowExecution = (workflowExecutionId?: string) => {
    const api = useGitpodAPI();

    return useQuery<PlainWorkflowExecution, Error>({
        queryKey: workflowQueryKey.executions.get(workflowExecutionId),
        queryFn: async () => {
            if (!workflowExecutionId) {
                throw new Error("No workflowExecutionId provided");
            }

            const { workflowExecution } = await api.workflowService.getWorkflowExecution({
                workflowExecutionId,
            });

            if (!workflowExecution) {
                throw new Error("Error fetching workflow execution");
            }

            return toPlainMessage(workflowExecution);
        },
        enabled: !!workflowExecutionId,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useGetWorkflowExecutionAction = (workflowExecutionActionId?: string) => {
    const api = useGitpodAPI();

    return useQuery<PlainWorkflowExecutionAction, Error>({
        queryKey: workflowQueryKey.executionAction(workflowExecutionActionId),
        queryFn: async () => {
            if (!workflowExecutionActionId) {
                throw new Error("No workflowExecutionActionId provided");
            }

            const { workflowExecutionAction } = await api.workflowService.getWorkflowExecutionAction({
                workflowExecutionActionId,
            });

            if (!workflowExecutionAction) {
                throw new Error("Error fetching workflow execution action");
            }

            return toPlainMessage(workflowExecutionAction);
        },
        enabled: !!workflowExecutionActionId,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export type CreateWorkflowParams = {
    name?: string;
    description?: string;
    executor?: PlainMessage<Subject>;
    triggers?: PlainWorkflowTrigger[];
    steps?: PlainWorkflowStep[];
    limits?: {
        maxParallel?: number;
        maxTotal?: number;
    };
};

export const useCreateWorkflow = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: CreateWorkflowParams) => {
            const defaultTriggers = [
                new WorkflowTrigger({
                    trigger: {
                        case: "manual",
                        value: new WorkflowTrigger_Manual(),
                    },
                    context: new WorkflowTriggerContext({
                        context: {
                            case: "projects",
                            value: new WorkflowTriggerContext_Projects({
                                projectIds: [],
                            }),
                        },
                    }),
                }),
            ];

            const defaultSteps = [
                new WorkflowStep({
                    step: {
                        case: "agent",
                        value: {
                            prompt: "With prompts you can send messages to Ona Agent",
                        },
                    },
                }),
                new WorkflowStep({
                    step: {
                        case: "task",
                        value: {
                            command: "echo 'with commands you can run shell commands'",
                        },
                    },
                }),
            ];

            const request = new CreateWorkflowRequest({
                name: params.name,
                description: params.description || "",
                triggers: params.triggers || defaultTriggers,
                executor: params.executor,
                action: new WorkflowAction({
                    limits: new WorkflowAction_Limits({
                        maxParallel: params.limits?.maxParallel ?? DEFAULT_WORKFLOW_LIMITS.maxParallel,
                        maxTotal: params.limits?.maxTotal ?? DEFAULT_WORKFLOW_LIMITS.maxTotal,
                    }),
                    steps: params.steps || defaultSteps,
                }),
            });
            const response = await api.workflowService.createWorkflow(request);
            if (!response.workflow) {
                throw new Error("Error creating workflow");
            }
            return response.workflow;
        },
        onSuccess: (workflow) => {
            if (workflow) {
                const plainWorkflow = toPlainWorkflow(workflow);
                setWorkflowInCache(queryClient, workflow.id, plainWorkflow);
            }
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export type UpdateWorkflowParams = {
    workflowId: string;
    name?: string;
    description?: string;
    executor?: PlainMessage<Subject>;
    action?: PlainWorkflowAction;
    triggers?: PlainWorkflowTrigger[];
};

export const useUpdateWorkflow = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ workflowId, name, description, executor, action, triggers }: UpdateWorkflowParams) => {
            const request = new UpdateWorkflowRequest({
                workflowId,
                name,
                description,
                executor,
                action,
                triggers,
            });
            const response = await api.workflowService.updateWorkflow(request);
            if (!response.workflow) {
                throw new Error("Failed to update workflow");
            }
            return toPlainWorkflow(response.workflow);
        },
        onSuccess: (updatedWorkflow) => {
            setWorkflowInCache(queryClient, updatedWorkflow.id, updatedWorkflow);
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useDeleteWorkflow = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ workflowId, force = false }: { workflowId: string; force?: boolean }) => {
            const request = new DeleteWorkflowRequest({ workflowId, force });
            return await api.workflowService.deleteWorkflow(request);
        },
        onSuccess: (_, { workflowId }) => {
            setWorkflowInCache(queryClient, workflowId, undefined);
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export type StartWorkflowParams = {
    workflowId: string;
    contextOverride?: PlainMessage<WorkflowTriggerContext>;
};

export const useStartWorkflow = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ workflowId, contextOverride }: StartWorkflowParams) => {
            const request = new StartWorkflowRequest({
                workflowId,
                contextOverride: contextOverride ? new WorkflowTriggerContext(contextOverride) : undefined,
            });
            const response = await api.workflowService.startWorkflow(request);
            if (!response.workflowExecution) {
                throw new Error("Failed to start workflow");
            }
            return toPlainMessage(response.workflowExecution);
        },
        onSuccess: (execution) => {
            setWorkflowExecutionInCache(queryClient, {
                workflowExecutionId: execution.id,
                workflowExecution: execution,
            });
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useCancelWorkflowExecution = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (execution: PlainWorkflowExecution) => {
            const request = new CancelWorkflowExecutionRequest({ workflowExecutionId: execution.id });
            await api.workflowService.cancelWorkflowExecution(request);
            return execution.id;
        },
        onSuccess: (_executionId: string, execution: PlainWorkflowExecution) => {
            if (execution.metadata?.workflowId) {
                setWorkflowExecutionInCache(queryClient, {
                    workflowExecutionId: execution.id,
                    workflowExecution: new WorkflowExecution({
                        ...execution,
                        status: { ...execution.status, phase: WorkflowExecutionPhase.STOPPING },
                    }),
                });
            }
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useCancelWorkflowExecutionAction = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (action: PlainWorkflowExecutionAction) => {
            const request = new CancelWorkflowExecutionActionRequest({
                workflowExecutionActionId: action.id,
            });
            await api.workflowService.cancelWorkflowExecutionAction(request);
            return action.id;
        },
        onSuccess: (_actionId: string, action: PlainWorkflowExecutionAction) => {
            if (action.metadata?.workflowExecutionId) {
                setWorkflowExecutionActionInCache(queryClient, {
                    workflowExecutionId: action.metadata.workflowExecutionId,
                    workflowExecutionActionId: action.id,
                    action: new WorkflowExecutionAction({
                        ...action,
                        status: {
                            ...action.status,
                            phase: WorkflowExecutionActionPhase.STOPPING,
                        },
                    }),
                });
            }
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export function setWorkflowInCache(client: QueryClient, workflowId: string, workflow?: PlainWorkflow) {
    client.setQueryData(workflowQueryKey.automations.get(workflowId), workflow ? workflow : null);
}

export function setWorkflowExecutionInCache(
    client: QueryClient,
    data: {
        workflowExecutionId: string;
        workflowExecution?: PlainWorkflowExecution;
    },
) {
    const { workflowExecutionId, workflowExecution } = data;
    client.setQueryData(
        workflowQueryKey.executions.get(workflowExecutionId),
        workflowExecution ? workflowExecution : null,
    );
}

export function setWorkflowExecutionActionInCache(
    client: QueryClient,
    data: {
        workflowExecutionId: string;
        workflowExecutionActionId: string;
        action?: PlainWorkflowExecutionAction;
    },
) {
    const { workflowExecutionId, workflowExecutionActionId, action } = data;
    client.setQueryData(workflowQueryKey.executionAction(workflowExecutionActionId), action ? action : null);
    client.setQueryData(
        workflowQueryKey.executionActions(workflowExecutionId),
        (currentData?: PlainWorkflowExecutionAction[]) => {
            if (!currentData) {
                return action ? [action] : currentData;
            }

            const updatedList = currentData
                .map((a: PlainWorkflowExecutionAction) => {
                    if (a.id !== workflowExecutionActionId) {
                        return a;
                    }
                    return action;
                })
                .filter((a) => !!a);

            // Add to the top of the list if it doesn't already exist
            if (action && !currentData.some((a) => a.id === workflowExecutionActionId)) {
                updatedList.unshift(action);
            }

            return updatedList;
        },
    );
}

export const useGetWorkflowWebhookSecret = (workflowId?: string) => {
    const api = useGitpodAPI();

    return useQuery({
        queryKey: workflowQueryKey.webhookSecret(workflowId),
        queryFn: async () => {
            if (!workflowId) {
                throw new Error("No workflowId provided");
            }

            const response = await api.workflowService.getWorkflowWebhookSecret({
                workflowId,
            });

            if (!response.secret) {
                throw new Error("Error fetching webhook secret");
            }

            return response.secret;
        },
        enabled: !!workflowId,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useRotateWorkflowWebhookSecret = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (workflowId: string) => {
            const response = await api.workflowService.rotateWorkflowWebhookSecret({
                workflowId,
            });

            if (!response.secret) {
                throw new Error("Failed to rotate webhook secret");
            }

            return response.secret;
        },
        onSuccess: (newSecret, workflowId) => {
            queryClient.setQueryData(workflowQueryKey.webhookSecret(workflowId), newSecret);
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export type PlainWorkflowExecutionActionOutput = PlainMessage<ListWorkflowExecutionOutputsResponse_ActionOutput>;

export type UseListWorkflowExecutionOutputsParams = {
    enabled: boolean;
    workflowExecutionId: string;
};

export const useListWorkflowExecutionOutputs = ({
    enabled,
    workflowExecutionId,
}: UseListWorkflowExecutionOutputsParams) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: workflowQueryKey.executionOutputs(workflowExecutionId),
        queryFn: async (): Promise<PlainWorkflowExecutionActionOutput[]> => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            const request = new ListWorkflowExecutionOutputsRequest({
                filter: { workflowExecutionIds: [workflowExecutionId] },
            });
            const outputs = await autoPaginate(
                (params) => api.workflowService.listWorkflowExecutionOutputs(params),
                request,
                (response) => response.outputs,
            );
            return outputs.map((output) => toPlainMessage(output));
        },
        enabled: enabled && !!user,
        throwOnError: false,
        retry: defaultRetry,
        refetchOnReconnect: "always",
        refetchOnWindowFocus: "always",
        refetchIntervalInBackground: true,
        refetchInterval: defaultRefetchLists,
    });
};

export const useCanCreateAutomation = () => {
    const { data: organization } = useOrganization();
    const { hasPermission: canCreateAutomations, isLoading } = useResourcePermission(
        ResourceType.ORGANIZATION,
        organization?.id || "",
        "workflow:create",
    );
    return {
        canCreateAutomations,
        isLoading,
    };
};

export const useCanEditAutomation = (workflowId: string | undefined) => {
    const { hasPermission: canEditAutomation, isLoading } = useResourcePermission(
        ResourceType.WORKFLOW,
        workflowId || "",
        "workflow:update",
    );
    return {
        canEditAutomation: workflowId ? canEditAutomation : false,
        isLoading,
    };
};

export const useCanDeleteAutomation = (workflowId: string | undefined) => {
    const { hasPermission: canDeleteAutomation, isLoading } = useResourcePermission(
        ResourceType.WORKFLOW,
        workflowId || "",
        "workflow:delete",
    );
    return {
        canDeleteAutomation: workflowId ? canDeleteAutomation : false,
        isLoading,
    };
};

export const useCanShareAutomation = (workflowId: string | undefined) => {
    const { hasPermission: canShareAutomation, isLoading } = useResourcePermission(
        ResourceType.WORKFLOW,
        workflowId || "",
        "workflow:grant",
    );
    return {
        canShareAutomation: workflowId ? canShareAutomation : false,
        isLoading,
    };
};

export const useCanRunAutomation = (workflowId: string | undefined) => {
    const { hasPermission: canRunAutomation, isLoading } = useResourcePermission(
        ResourceType.WORKFLOW,
        workflowId || "",
        "workflowexecution:create",
    );
    return {
        canRunAutomation: workflowId ? canRunAutomation : false,
        isLoading,
    };
};
